import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  InputAdornment,
  useTheme,
  alpha,
  SelectChangeEvent,
} from "@mui/material";
import {
  parsePhoneNumber,
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";

// Type pour inclure le séparateur
type CountryCodeOrSeparator = CountryCode | "separator";

// Liste des pays avec leurs informations
interface CountryData {
  code: CountryCodeOrSeparator;
  name: string;
  dialCode: string;
  flag: string;
}

// Fonction pour obtenir l'emoji du drapeau à partir du code pays
const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Définition des pays les plus courants pour Safee
const getCountryData = (): CountryData[] => {
  const countries: CountryData[] = [];
  const allCountries = getCountries();

  // Ajouter la France en premier
  const dialCodeFR = getCountryCallingCode("FR");
  countries.push({
    code: "FR",
    name: getCountryName("FR"),
    dialCode: `+${dialCodeFR}`,
    flag: getFlagEmoji("FR"),
  });

  // Ajouter une ligne de séparation
  countries.push({
    code: "separator",
    name: "───────────────",
    dialCode: "",
    flag: "",
  });

  // Ajouter tous les autres pays triés par nom
  allCountries
    .filter((code) => code !== "FR")
    .sort((a, b) => getCountryName(a).localeCompare(getCountryName(b)))
    .forEach((code) => {
      const dialCode = getCountryCallingCode(code);
      countries.push({
        code,
        name: getCountryName(code),
        dialCode: `+${dialCode}`,
        flag: getFlagEmoji(code),
      });
    });

  return countries;
};

// Fonction pour obtenir le nom du pays en français via l'API Intl
const getCountryName = (code: CountryCode): string => {
  try {
    // Utiliser l'API native Intl.DisplayNames pour obtenir les noms de pays en français
    const displayNames = new Intl.DisplayNames(['fr'], { type: 'region' });
    return displayNames.of(code) || code;
  } catch (error) {
    // Fallback sur le code pays si l'API échoue
    return code;
  }
};

interface CountryPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const CountryPhoneInput: React.FC<CountryPhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  error = false,
  helperText = "",
  required = false,
  label = "Numéro de téléphone",
  placeholder = "6 12 34 56 78",
  disabled = false,
}) => {
  const theme = useTheme();
  const countries = useMemo(() => getCountryData(), []);

  // État pour le pays sélectionné
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("FR");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Initialiser les valeurs à partir de la prop value si elle existe
  React.useEffect(() => {
    if (value && value.startsWith("+")) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed) {
          setSelectedCountry(parsed.country as CountryCode);
          setPhoneNumber(parsed.nationalNumber);
        }
      } catch (e) {
        // Si le parsing échoue, on garde les valeurs par défaut
      }
    }
  }, []);

  // Obtenir le code pays actuellement sélectionné
  const currentCountryData = useMemo(
    () => countries.find((c) => c.code === selectedCountry),
    [countries, selectedCountry]
  );

  // Gérer le changement de pays
  const handleCountryChange = (event: SelectChangeEvent<CountryCodeOrSeparator>) => {
    const newCountry = event.target.value as CountryCodeOrSeparator;
    if (newCountry === "separator") return; // Ignorer la ligne de séparation

    setSelectedCountry(newCountry as CountryCode);

    // Mettre à jour la valeur complète
    if (phoneNumber) {
      const dialCode = getCountryCallingCode(newCountry as CountryCode);
      const cleanNumber = phoneNumber.replace(/^0+/, ""); // Retirer les zéros initiaux
      onChange(`+${dialCode}${cleanNumber}`);
    }
  };

  // Gérer le changement du numéro
  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    // Ne garder que les chiffres et les espaces
    const cleaned = input.replace(/[^\d\s]/g, "");
    setPhoneNumber(cleaned);

    // Mettre à jour la valeur complète
    const dialCode = getCountryCallingCode(selectedCountry);
    const cleanNumber = cleaned.replace(/\s/g, "").replace(/^0+/, ""); // Retirer espaces et zéros initiaux

    if (cleanNumber) {
      onChange(`+${dialCode}${cleanNumber}`);
    } else {
      onChange("");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", gap: 1 }}>
        {/* Sélecteur de pays */}
        <FormControl
          sx={{
            minWidth: 140,
            maxWidth: 140,
          }}
          error={error}
          disabled={disabled}
        >
          <Select
            value={selectedCountry}
            onChange={handleCountryChange}
            sx={{
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                gap: 1,
              },
            }}
            renderValue={(selected) => {
              const country = countries.find((c) => c.code === selected);
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span style={{ fontSize: "1.5em" }}>{country?.flag}</span>
                  <span>{country?.dialCode}</span>
                </Box>
              );
            }}
          >
            {countries.map((country) => (
              <MenuItem
                key={country.code}
                value={country.code}
                disabled={country.code === "separator"}
                sx={{
                  ...(country.code === "separator" && {
                    cursor: "default",
                    pointerEvents: "none",
                    "&:hover": {
                      bgcolor: "transparent",
                    },
                  }),
                }}
              >
                {country.code !== "separator" ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                    <span style={{ fontSize: "1.5em", minWidth: "1.5em" }}>
                      {country.flag}
                    </span>
                    <span style={{ flex: 1 }}>{country.name}</span>
                    <span
                      style={{
                        color: alpha(theme.palette.text.primary, 0.6),
                        fontSize: "0.9em",
                      }}
                    >
                      {country.dialCode}
                    </span>
                  </Box>
                ) : (
                  <span style={{ color: theme.palette.divider }}>
                    {country.name}
                  </span>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Input du numéro de téléphone */}
        <TextField
          fullWidth
          required={required}
          label={label}
          value={phoneNumber}
          onChange={handlePhoneChange}
          onBlur={onBlur}
          error={error}
          placeholder={placeholder}
          disabled={disabled}
        />
      </Box>

      {/* Message d'aide ou d'erreur */}
      {helperText && (
        <FormHelperText error={error} sx={{ mx: "14px", mt: "3px" }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};
